/**
 * The right-hand column of the call dialog: everything about the call that is not the call.
 *
 * IT USED TO BE A PAGE, and most of this file is that page's components moved sideways
 * rather than rewritten - the reasoning in each one still holds, because what changed is
 * the geometry and not the question. The page laid three short cards ACROSS because none
 * of them filled 1400px; this is 40% of 1180px, so the same content runs DOWN, and the
 * cards become sections.
 *
 * SECTIONS, NOT PANELS - WITH TWO EXCEPTIONS, AND THE EXCEPTIONS ARE THE POINT. `Panel` is
 * a bordered white card, and five of them stacked in a 440px column inside a bordered
 * dialog is a box in a box in a box. That is still true of the prose sections here, which
 * are separated by a hairline and a small capital heading and nothing else.
 *
 * The two that ARE boxed - how the caller sounded, and what to do about the call - are the
 * two that are not prose. One is a state with a colour, the other is the instruction
 * somebody opened this call to find, and both were previously indistinguishable from the
 * paragraphs above and below them. The column is 576px rather than 440px now, which is what
 * makes a box in it read as one object rather than as a narrow strip.
 *
 * WHAT IS NOT HERE ANY MORE: the caller's name and number. They are in the dialog's header,
 * where they belong - it is the record's identity, it is a `tel:` link, and it survives this
 * column being scrolled. Printing them again 300px lower was one of the repetitions this
 * reshape exists to remove.
 */

import { useState, type ReactNode } from "react";
import type { CallDetail as Call } from "../api";
import { QualificationDetail, hasQualification } from "./Qualification";
import { Fields } from "./Fields";
import { Copy, Check, Frown, Meh, Smile } from "./Icons";
import { MOOD_FILL, MOOD_INK, MOOD_LABELS, MOOD_TINT, isMood, type Mood } from "../charts";
import { useCurrentAgent } from "../currentAgent";
import { Basis, Button, Pair, Pairs, cx } from "../ui";
import { basisLabel, duration, labelFor, money } from "../format";

/**
 * One band of the column.
 *
 * `h3`, because the dialog's title is the `h2` and a reading is inside a call rather than
 * beside it. The type, tracking and ink are `Fields.tsx`'s own `Section` - the same idea,
 * one level up - so nothing new is introduced to look at.
 *
 * THE INK IS `soft`, NOT `ghost`. These labels are 11px small capitals, which is the
 * smallest type in the product and the hardest to read, and the ramp had them set in the
 * lightest step it has - "the quietest thing allowed to be words at all" - so a column of
 * six headings faded into the rules under them. Quiet is right for a label; that quiet was
 * not. The dialog's own "The conversation" moved with them, since they head two halves of
 * one screen and must not disagree.
 */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-t border-line-soft pt-3.5 first:border-t-0 first:pt-0">
      <h3 className="mb-2 text-2xs font-semibold tracking-[0.06em] text-ink-soft uppercase">
        {title}
      </h3>
      {children}
    </section>
  );
}

export function CallReading({ call }: { call: Call }) {
  const { current } = useCurrentAgent();
  const words = current?.vocabulary.labels ?? {};
  const detail = call.outcomeDetail ?? {};
  const fields = (detail.fields ?? {}) as Record<string, unknown>;
  const card = typeof detail.card === "string" ? detail.card : null;
  const unanswered = questions(call);

  // A call nobody spoke on gets a sentence, not six headings over "not given". The
  // transcript is still on the left, which is what tells the two apart.
  if (!hasSubstance(call, fields, unanswered)) return <Nothing call={call} />;

  return (
    <div className="grid gap-3.5">
      {/* THE ONE ACTION, AT THE TOP. There is no footer in this dialog, and an action under
          six sections is an action nobody scrolls to. */}
      {card && <CopyButton className="justify-center" text={card} />}

      {call.analysis.summary ? (
        <Section title="Call summary">
          <p className="text-base leading-relaxed text-ink">{call.analysis.summary}</p>
          <Reading call={call} />
        </Section>
      ) : call.analysis.status === "pending" ? (
        <Section title="Call summary">
          <p className="text-sm text-ink-faint">
            Still reading this call. The summary usually lands within a minute of the caller
            hanging up.
          </p>
        </Section>
      ) : null}

      <Sounded call={call} />
      <Where call={call} />
      {hasOutcome(call) && (
        <Section title="Call outcome">
          <Outcome call={call} />
        </Section>
      )}

      {/* Asked before the heading is drawn, not inside the component: a panel that renders
          nothing still leaves its own title on screen, which is a heading over an empty box
          - the one thing this column is arranged to avoid. */}
      {hasQualification(call.analysis.qualification) && (
        <Section title="Enquiry strength">
          <QualificationDetail qualification={call.analysis.qualification} labels={words} />
        </Section>
      )}

      {unanswered.length > 0 && (
        <Section title="What the agent couldn't answer">
          {/*
            First class on purpose - this is the one thing on the screen that tells an
            owner something about their own business they did not already know - so it is
            set at the summary's size and in full ink rather than as a footnote.

            A RULE DOWN THE SIDE, NOT A HANGING QUOTE MARK. The mark that used to sit here
            claimed two things that are not true. It opened a quotation nothing closed, and
            it presented these as verbatim speech when the analyser is asked for the
            question and returns a reported clause - "whether the health fund rebate
            applies to a replacement crown" is nobody's spoken sentence. The rule also does
            the job the glyph could not: it holds the second and third lines of a long
            question together as one block instead of leaving them adrift under a floating
            character.
          */}
          <ul className="grid list-none gap-3 p-0">
            {unanswered.map((question) => (
              <li key={question} className="border-l-2 border-line pl-3">
                <p className="text-base leading-snug text-ink">{question}</p>
              </li>
            ))}
          </ul>
        </Section>
      )}

      {Object.keys(fields).length > 0 && (
        /* The agent's own bag - the platform never learns what a `Grade` or a `healthFund`
           is - rendered by SHAPE rather than from a configured list of keys, so an agent
           that ships a new field on Tuesday sees it laid out properly on Tuesday. */
        <Section title="Structured details">
          <Fields bare fields={fields} currency={current?.currency} />
        </Section>
      )}
    </div>
  );
}

/**
 * What the reading found, beside the summary it belongs to.
 *
 * EVERY REASON, THE MAIN ONE FIRST, in the order the reading gave them. `other` is
 * suppressed, and that is not tidying: it is the analyser saying it could not place the call
 * in the business's taxonomy - the ABSENCE of a category - and rendering it as "other"
 * presents a failure to classify as a classification. It only ever stands alone, so a call
 * read as `other` shows no line at all.
 *
 * IT USED TO CARRY "SOUNDED" TOO. The mood has its own card now, directly below; a pair row
 * reading "Sounded  Positive" three lines above a card reading "CALLER SOUNDED  Positive"
 * is the same fact twice in one column, and the reader spends a second working out whether
 * the two are saying something different.
 */
function Reading({ call }: { call: Call }) {
  // The business's own word for the category, so this says "Repower" where the overview's
  // bar and the call log's row both say "Repower". `humanise` rendered the raw key, which
  // was a third spelling of the same thing on the third screen.
  const words = useCurrentAgent().current?.vocabulary.labels;
  const intents = (call.analysis.intents ?? []).filter((intent) => intent !== "other");

  if (!intents.length) return null;
  return (
    <Pairs className="mt-3.5">
      <Pair label="Rang about">{intents.map((intent) => labelFor(words, intent)).join(", ")}</Pair>
    </Pairs>
  );
}

/** The three faces, and the sentence each of them is short for. */
const MOOD_FACE: Record<Mood, typeof Smile> = {
  positive: Smile,
  neutral: Meh,
  negative: Frown,
};

const MOOD_NOTE: Record<Mood, string> = {
  positive: "Enthusiastic, satisfied or appreciative.",
  neutral: "Balanced and factual, neither warm nor unhappy.",
  negative: "Frustrated, unhappy, or raising concerns.",
};

/**
 * How the caller sounded, as the one coloured object in this column.
 *
 * IT IS A CARD BECAUSE IT IS A STATE RATHER THAN A SENTENCE. Everything else here is prose
 * about one call; this is one of three values, it is the same three on every call, and it
 * is the thing an owner skimming a morning's calls is looking for. As a pair row buried
 * under the summary it was a grey word among grey words.
 *
 * THE COLOUR COMES FROM `charts.tsx` AND IS NOT NAMED HERE. That file is "the only file
 * that knows a colour", and a green invented in this one is the second green: it and the
 * overview's donut would drift within a month. `MOOD_FILL` is what the donut paints with
 * and is used here only for the dot, because those three are chart fills and all three fail
 * AA as words - `MOOD_INK` and `MOOD_TINT` beside them are the checked text pairs.
 *
 * `neutral` ON A CALL WHERE NOBODY SPOKE IS NOT A MOOD. It is the analysis schema's
 * `.catch()` default showing through, and drawing a card that says the caller sounded
 * balanced when the caller said nothing at all is the kind of confident wrong answer that
 * costs a dashboard its credibility. This guard moved here with the fact it guards.
 */
function Sounded({ call }: { call: Call }) {
  const mood = call.analysis.sentiment;
  if (!isMood(mood)) return null;
  const spoke = call.transcript.some((turn) => turn.role === "caller");
  if (mood === "neutral" && !spoke) return null;

  const Face = MOOD_FACE[mood];
  return (
    <section className={cx("flex items-center gap-3.5 rounded-lg border px-4 py-3", MOOD_TINT[mood])}>
      <span
        className={cx(
          "grid size-9 flex-none place-items-center rounded-full bg-paper",
          MOOD_INK[mood],
        )}
      >
        <Face size={19} />
      </span>
      <div className="grid min-w-0 gap-0.5">
        <h3 className={cx("text-2xs font-semibold tracking-[0.06em] uppercase", MOOD_INK[mood])}>
          Caller sounded
        </h3>
        <span className="flex items-center gap-2 text-base font-semibold text-ink">
          {/* The donut's own colour, as the one shape on this card that is allowed to carry
              it. Somebody who clicked through from that chart should see it agree. */}
          <span aria-hidden="true" className={cx("size-2 rounded-full", MOOD_FILL[mood])} />
          {MOOD_LABELS[mood]}
        </span>
      </div>
      <span className="ml-auto max-w-50 text-right text-sm leading-snug text-ink-soft @max-[420px]:hidden">
        {MOOD_NOTE[mood]}
      </span>
    </section>
  );
}

/**
 * Where the caller was - which is all that is left of "Who called".
 *
 * NO AVATAR, and the reasoning survives the move: there is no photograph and there never
 * will be, and a circle holding the last two digits of a phone number is invented identity.
 * The name and the number are in the dialog's header now, so what is left here is the one
 * fact that did not fit up there, and the honest sentence for a call that captured nothing.
 */
function Where({ call }: { call: Call }) {
  const { name, location, phone: number } = call.contact;
  if (location) {
    return (
      <Section title="Where">
        <p className="text-sm text-ink-soft">{location}</p>
      </Section>
    );
  }
  // Three blanks read as a page that failed to load; one sentence reads as a fact about
  // the call. Only said when there is genuinely nothing - the header covers the rest.
  if (name || number || call.caller || call.callee) return null;
  return (
    <Section title="Who called">
      <p className="text-sm text-ink-faint">The agent didn't get a name, a number or a location.</p>
    </Section>
  );
}

/**
 * What to do about it - the section that only exists when there is something to put in it.
 *
 * THE HEADER BADGE IS THE GRADE AND THIS IS THE INSTRUCTION, which is why the badge is not
 * repeated here. `value` is what the agent thought the call was worth, and
 * `analysis.followUp` is the analyser's one sentence for whoever picks it up - the only
 * screen with room for it, which is why `api.ts` carries it on the detail response and not
 * on the list row.
 *
 * THE HANDOFF CHIP HAS GONE FROM HERE and is in the dialog's header, where it reads as part
 * of the record's identity - "handed off by sms" - rather than as an unlabelled grey word
 * above a figure. `hasOutcome` still counts it, because a call whose only outcome is where
 * it was sent should still say so somewhere the reader is looking.
 *
 * IT IS BOXED, unlike the prose sections around it. This is the answer to "what do I do
 * with this call", which is the question the dialog was opened to settle; a headline, a
 * number and a next step set as three loose paragraphs read as more of the summary above.
 *
 * `hasOutcome` lives outside the component because the caller has to know whether there is
 * anything here before it draws a heading over it.
 */
function hasOutcome(call: Call): boolean {
  return Boolean(call.outcome.handoff || call.outcome.value || call.analysis.followUp);
}

function Outcome({ call }: { call: Call }) {
  const { current } = useCurrentAgent();
  const { headline, value } = call.outcome;
  const followUp = call.analysis.followUp;

  return (
    <div className="rounded-lg border border-line bg-sunken px-4 py-3.5">
      {(headline || value) && (
        // ONE BASELINE. The agent's own line about the call and what it thought the call
        // was worth are one statement - "sized a setup, five thousand eight hundred" - and
        // stacking them makes the reader join two lines that were meant to be read as one.
        <div className="mb-2 flex items-baseline justify-between gap-4">
          {headline && (
            <span className="min-w-0 text-base font-semibold text-ink">{headline}</span>
          )}
          {value && (
            <span className="nums flex-none text-right font-mono text-base font-semibold text-ink">
              {money(value.minor, value.currency ?? current?.currency ?? null)}
              {/* Never bare. A quote read out over the phone is not revenue, and a figure
                  that mixes the two silently is a dashboard that lies about money. */}
              <Basis>{basisLabel(value.basis)}</Basis>
            </span>
          )}
        </div>
      )}
      {followUp ? (
        <p className="text-sm leading-normal text-ink-soft">{followUp}</p>
      ) : (
        !headline &&
        !value && (
          // `hasOutcome` let us in on the handoff alone, so the box would otherwise be
          // empty. The header says where it went; this says that is all there is.
          <p className="text-sm text-ink-faint">
            Nothing was quoted or promised. The call was handed on as it stood.
          </p>
        )
      )}
    </div>
  );
}

/** The whole column, when there is no column. */
function Nothing({ call }: { call: Call }) {
  const spoke = call.transcript.some((turn) => turn.role === "caller");
  return (
    <div className="rounded-lg border border-line bg-sunken px-4 py-3.5">
      <p className="text-base font-semibold text-ink">
        {spoke ? "Nothing was established" : "Nobody spoke"}
      </p>
      <p className="mt-1 text-sm text-ink-soft">
        {/* `durationMs` is null on a call that never reported one, and "hung up — in"
            is worse than not saying when. The sentence loses the clause, not the fact. */}
        {spoke
          ? `The call ended before the caller said what they wanted. There is nothing here to act on.`
          : call.durationMs
            ? `The caller hung up ${duration(call.durationMs)} in, before saying what they wanted. No name or number was captured.`
            : `The caller hung up before saying what they wanted. No name or number was captured.`}
      </p>
    </div>
  );
}

/**
 * Is there a reading here at all?
 *
 * The same floor the overview applies before it draws a chart: below it, the honest
 * rendering of the data is a sentence about its absence, and drawing the full scaffolding
 * anyway produces six headings that between them say "not given" eleven times. A third of
 * calls in the archive end before reaching a resolution, so this is the COMMON case, not
 * an edge one.
 */
function hasSubstance(
  call: Call,
  fields: Record<string, unknown>,
  unanswered: string[],
): boolean {
  if (call.outcome.label || call.outcome.value || call.outcome.handoff) return true;
  if (call.contact.name || call.contact.phone || call.contact.location) return true;
  if (unanswered.length || Object.keys(fields).length) return true;
  return call.transcript.some((turn) => turn.role === "caller");
}

/**
 * The questions the agent could not answer, from both the agent and the analyser.
 *
 * Two sources on purpose - the agent's own list in its outcome and the analyser's in the
 * reading - but an owner reading a list does not care which half of the system noticed, so
 * they are merged and de-duplicated here rather than shown as two sections that are the
 * same section.
 */
function questions(call: Call): string[] {
  const fromAgent = (call.outcomeDetail?.unanswered ?? []) as string[];
  const fromReading = (call.analysis.unanswered ?? []).map((item) =>
    typeof item === "string" ? item : item.question,
  );
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of [...fromAgent, ...fromReading]) {
    const question = item.trim();
    if (!question) continue;
    const key = question.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(question);
  }
  return out;
}

/**
 * The lead card, as a button rather than a panel.
 *
 * Every fact in the card is already on this screen in a form nobody has to decode, and what
 * the card is FOR is pasting into a text message or a CRM. So nothing here is a preview of
 * what the button will put on the clipboard.
 */
export function CopyButton({ text, className }: { text: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      className={className}
      onClick={() => {
        void navigator.clipboard?.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1600);
      }}
      title="Copy the card so it can be pasted into an email or a CRM"
    >
      {copied ? <Check /> : <Copy />}
      {/* The label changes to the past tense of the same verb. "Copy" then "Copied" is
          the whole confirmation anybody needs, and it costs no toast. */}
      {copied ? "Copied" : "Copy lead card"}
    </Button>
  );
}
